import { useAuth } from "../contexts/useAuth";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/TranslationContext";
import { ChevronDown } from "lucide-react";

function Practice() {
  const { token, isPaid } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [evaluation, setEvaluation] = useState("");
  const [status, setStatus] = useState(t("practice.statusInitial"));
  const [loading, setLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [questionType, setQuestionType] = useState("technical");
  const [previousQuestions, setPreviousQuestions] = useState([]);
  const answerRef = useRef(null);

  useEffect(() => {
    if (!token || !isPaid) { navigate("/"); return; }
  }, [token, isPaid, navigate]);

  const generateQuestion = async () => {
    setLoading(true);
    setQuestion("");
    setAnswer("");
    setEvaluation("");
    setStatus(t("practice.statusGenerating"));

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/gpt/chat`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            { role: "system", content: "Voce e um entrevistador tecnico senior. Gere APENAS uma pergunta de entrevista, sem contexto nem explicacao." },
            { role: "user", content: `Gere uma pergunta ${
              questionType === 'technical' ? 'TECNICA sobre programacao/arquitetura' :
              questionType === 'behavioral' ? 'COMPORTAMENTAL (Tell me about a time...)' :
              questionType === 'motivational' ? 'MOTIVACIONAL sobre carreira' :
              'de LOGICA/ESTIMATIVA (brain teaser)'
            }. ${previousQuestions.length > 0 ? `NAO repita: ${previousQuestions.join('; ')}` : ''}` }
          ],
          model: "gpt-4o-mini",
          max_tokens: 200,
          temperature: 0.9,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) fullText += delta;
          } catch (_) {}
        }
      }

      setQuestion(fullText.trim());
      setPreviousQuestions(prev => [...prev, fullText.trim()]);
      setStatus(t("practice.statusGenerated"));

      // TTS
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(fullText.trim());
        utterance.lang = "en-US";
        utterance.rate = 0.95;
        window.speechSynthesis.speak(utterance);
      }
    } catch (err) {
      setStatus(t("practice.errorPrefix") + err.message);
    } finally {
      setLoading(false);
    }
  };

  const evaluateAnswer = async () => {
    if (!answer.trim()) return;
    setEvaluating(true);
    setEvaluation("");
    setStatus(t("practice.statusEvaluating"));

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/gpt/chat`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [
            { role: "system", content: `Avalie a resposta de entrevista. De nota 1-10 em CONTEUDO, COMUNICACAO e RELEVANCIA. Depois feedback: o que foi BOM, o que MELHORAR, uma DICA.` },
            { role: "user", content: `PERGUNTA: ${question}\n\nRESPOSTA: ${answer}` }
          ],
          model: "gpt-4o-mini",
          max_tokens: 500,
          temperature: 0.7,
        }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") break;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              fullText += delta;
              setEvaluation(fullText);
            }
          } catch (_) {}
        }
      }

      setStatus(t("practice.statusEvalComplete"));
    } catch (err) {
      setStatus(t("practice.errorPrefix") + err.message);
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
      <div className="max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-white mb-6">Modo Pratica</h1>

        <div className="bg-[#111] border border-white/10 rounded-lg p-6">
          <p className="text-gray-400 text-sm mb-6">Treine para entrevistas — IA faz perguntas e avalia suas respostas.</p>

          {/* Controls */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="relative">
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className="appearance-none bg-[#0a0a0a] border border-white/10 text-white pl-3 pr-9 py-2 rounded-lg text-sm cursor-pointer"
              >
                <option value="technical">{t("practice.typeTechnical")}</option>
                <option value="behavioral">{t("practice.typeBehavioral")}</option>
                <option value="motivational">{t("practice.typeMotivational")}</option>
                <option value="brainteaser">{t("practice.typeBrainTeaser")}</option>
              </select>
              <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
            </div>
            <button
              onClick={generateQuestion}
              disabled={loading}
              className="bg-brand-500 hover:bg-brand-400 text-black font-semibold px-4 py-2 rounded-lg text-sm transition disabled:opacity-50"
            >
              {loading ? t("practice.generating") : t("practice.generateButton")}
            </button>
          </div>

          {/* Status */}
          <p className="text-gray-400 text-xs mb-4">{status}</p>

          {/* Question */}
          {question && (
            <div className="bg-[#0a0a0a] border border-brand-500/20 rounded-lg p-4 mb-4">
              <h3 className="text-xs text-brand-500 font-semibold mb-2">{t("practice.questionLabel")}</h3>
              <p className="text-white text-sm">{question}</p>
            </div>
          )}

          {/* Answer */}
          {question && (
            <div className="mb-4">
              <h3 className="text-xs text-gray-400 font-semibold mb-2">{t("practice.yourAnswerLabel")}</h3>
              <textarea
                ref={answerRef}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={t("practice.answerPlaceholder")}
                className="w-full bg-[#0a0a0a] border border-white/10 text-white p-4 rounded-lg text-sm min-h-[120px] resize-y focus:border-brand-500/50 outline-none"
              />
              <button
                onClick={evaluateAnswer}
                disabled={evaluating || !answer.trim()}
                className="mt-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-lg text-sm transition border border-white/20 disabled:opacity-50"
              >
                {evaluating ? t("practice.evaluating") : t("practice.evaluateButton")}
              </button>
            </div>
          )}

          {/* Evaluation */}
          {evaluation && (
            <div className="bg-[#0a0a0a] border border-green-500/20 rounded-lg p-4">
              <h3 className="text-xs text-green-400 font-semibold mb-2">{t("practice.evaluationLabel")}</h3>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{evaluation}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Practice;
